import type { CharacterProgression } from "@/game/model/character-progression";
import { getGameSettings } from "@/game/model/game-settings";

export type StatId =
  | "attack"
  | "defense"
  | "critRate"
  | "critDamage"
  | "dodge"
  | "attackSpeed"
  | "moveSpeed"
  | "hpRegen"
  | "mana";

export const STAT_IDS: StatId[] = [
  "attack",
  "defense",
  "critRate",
  "critDamage",
  "dodge",
  "attackSpeed",
  "moveSpeed",
  "hpRegen",
  "mana",
];

export const STAT_LABEL_TH: Record<StatId, string> = {
  attack: "โจมตี",
  defense: "ป้องกัน",
  critRate: "อัตราคริ",
  critDamage: "ความแรงคริ",
  dodge: "หลบ",
  attackSpeed: "ความเร็วโจมตี",
  moveSpeed: "ความเร็วเคลื่อนที่",
  hpRegen: "ฟื้นเลือด",
  mana: "มานา",
};

/** ผลเมื่อกด + สะสมแต้ม (อิงสูตรจากค่าพื้นฐานเกม) */
export const STAT_PLUS_HINT_TH: Record<StatId, string> = {
  attack: "+ เยอะ → ดาเมจและความแม่นยำสูงขึ้น",
  defense: "+ เยอะ → โดนตีเจ็บน้อยลง",
  critRate: "+ เยอะ → คริติดบ่อยขึ้น",
  critDamage: "+ เยอะ → ดาเมจคริแรงขึ้น",
  dodge: "+ เยอะ → หลบการโจมตีได้บ่อยขึ้น",
  attackSpeed: "+ เยอะ → หน่วงหลังตีสั้นลง (100 AP = ถึงหน่วงต่ำสุดใน GM)",
  moveSpeed: "+ เยอะ → เดิน/วิ่งในฉากเร็วขึ้น",
  hpRegen: "+ เยอะ → ฟื้น HP ต่อวินาทีเร็วขึ้น",
  mana: "+ เยอะ → มานาสูงสุดเพิ่ม",
};

export type StatAllocation = Record<StatId, number>;

/** Optional per-stat caps (unset = no cap). */
export type StatCaps = Partial<Record<StatId, number>>;

export function emptyAllocation(): StatAllocation {
  return {
    attack: 0,
    defense: 0,
    critRate: 0,
    critDamage: 0,
    dodge: 0,
    attackSpeed: 0,
    moveSpeed: 0,
    hpRegen: 0,
    mana: 0,
  };
}

export function mergeAllocations(
  base: StatAllocation,
  bonus: Partial<StatAllocation> | undefined,
): StatAllocation {
  if (!bonus) return { ...base };
  const out = { ...base };
  for (const id of STAT_IDS) {
    const extra = bonus[id];
    if (typeof extra === "number" && extra > 0) {
      out[id] += Math.floor(extra);
    }
  }
  return out;
}

export type CombatRatings = {
  level: number;
  attack: number;
  defense: number;
  dodge: number;
  hit: number;
  critChance: number;
  critMultiplier: number;
  attackSpeed: number;
  moveSpeed: number;
  /** HP restored per second (base from GM + stat). */
  hpRegenPerSec: number;
  /** Points from mana stat (max mana scaling). */
  manaMaxBonus: number;
};

export function ratingsFromAllocation(
  level: number,
  allocated: StatAllocation,
): CombatRatings {
  const atkPts = allocated.attack;
  const defPts = allocated.defense;
  const dodgePts = allocated.dodge;
  const critRatePts = allocated.critRate;
  const critDmgPts = allocated.critDamage;

  const s = getGameSettings();
  return {
    level,
    attack: s.playerBaseAttack + atkPts * s.playerAttackPerStat,
    defense: defPts * s.playerDefensePerStat,
    dodge: s.playerBaseDodge + dodgePts * s.playerDodgePerStat + level * s.playerDodgePerLevel,
    hit: s.playerBaseHit + level * s.playerHitPerLevel + atkPts * s.playerHitPerAttackStat,
    critChance: Math.min(1, s.playerBaseCritChance + critRatePts * s.playerCritChancePerStat),
    critMultiplier: s.playerBaseCritMultiplier + critDmgPts * s.playerCritMultPerStat,
    attackSpeed: allocated.attackSpeed,
    moveSpeed: allocated.moveSpeed,
    hpRegenPerSec:
      s.playerBaseHpRegenPerSec + allocated.hpRegen * s.playerHpRegenPerStat,
    manaMaxBonus: allocated.mana,
  };
}

export function mergeStatBonuses(
  ...bonuses: Array<Partial<StatAllocation> | undefined>
): Partial<StatAllocation> | undefined {
  const out: Partial<StatAllocation> = {};
  for (const bonus of bonuses) {
    if (!bonus) continue;
    for (const id of STAT_IDS) {
      const extra = bonus[id];
      if (typeof extra === "number" && extra > 0) {
        out[id] = (out[id] ?? 0) + Math.floor(extra);
      }
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function playerRatings(
  progression: CharacterProgression,
  buffBonus?: Partial<StatAllocation>,
): CombatRatings {
  const allocated = mergeAllocations(progression.allocated, buffBonus);
  return ratingsFromAllocation(progression.level, allocated);
}

/** Max combat HP (tower battle — keep in sync with TowerScene). */
export function playerCombatMaxHp(ratings: CombatRatings): number {
  return Math.floor(180 + ratings.level * 70 + ratings.defense * 2.5);
}

/**
 * Display combat power from derived ratings (HUD / compare). Tune weights in game-settings later if needed.
 */
export function characterCombatPower(ratings: CombatRatings, maxHp: number): number {
  const attackWithCrit =
    ratings.attack * (1 + ratings.critChance * (ratings.critMultiplier - 1));
  const speedPts = Math.max(0, ratings.attackSpeed);
  const movePts = Math.max(0, ratings.moveSpeed);
  const raw =
    attackWithCrit * 3 +
    ratings.defense * 2 +
    maxHp * 0.15 +
    ratings.hit * 0.5 +
    ratings.dodge * 0.5 +
    ratings.hpRegenPerSec * 20 +
    speedPts * 1.5 +
    movePts * 1.2;
  return Math.max(0, Math.floor(raw));
}

/**
 * Ms after normal attack anim before the next strike.
 * Lv.1 + 0 AP uses baseAttackGapMs; 100 AP (config) linearly reaches minAttackGapMs;
 * each level above 1 also reduces gap (shared budget with AP).
 */
export function attackGapMs(level: number, attackSpeedPoints: number): number {
  const s = getGameSettings();
  const lvl = Math.max(1, Math.floor(level));
  const pts = Math.max(0, Math.floor(attackSpeedPoints));
  const floor = Math.min(s.baseAttackGapMs, s.minAttackGapMs);
  const ceiling = Math.max(s.baseAttackGapMs, s.minAttackGapMs);
  const span = ceiling - floor;
  const apCap = Math.max(1, s.attackSpeedStatForMinGap);
  const fromAp = span * Math.min(1, pts / apCap);
  const fromLevel = (lvl - 1) * s.attackGapReductionPerLevel;
  const reduction = Math.min(span, fromAp + fromLevel);
  return Math.max(floor, ceiling - reduction);
}

/** Multiplier for walk tween duration (higher = faster). */
export function moveSpeedMultiplier(moveSpeedPoints: number): number {
  const s = getGameSettings();
  return 1 + moveSpeedPoints * s.moveSpeedMultPerStat;
}
